import React from 'react';
import {
  Badge,
  BadgeText,
  Box,
  Button,
  ButtonSpinner,
  ButtonText,
  Center,
  HStack,
  Icon,
  Menu,
  MenuItem,
  MenuItemLabel,
  Pressable,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useConnectionQualityIndicator,
  useConnectionState,
  useMaybeRoomContext,
  useRemoteParticipants,
  useTracks,
  VideoTrack,
  type TrackReference,
} from '@livekit/components-react';
import {
  ConnectionQuality, ConnectionState, DisconnectReason, Track, type Participant,
} from 'livekit-client';
import {
  CheckCircle,
  Clock,
  MoreVertical,
  RotateCcw,
  UserMinus,
  UserX,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import type { OralExamBookingRunSummary } from '@/common/api/models/oralExamBookingRunSummary';
import { OralExamConductPhase } from '@/common/api/models/oralExamConductPhase';
import { OralExamPauseReason } from '@/common/api/models/oralExamPauseReason';
import { useMeetingConfirm } from '@/common/components/meeting/MeetingConfirm';
import { useMeetingOpenElsewhereOptional } from '@/common/components/meeting/MeetingOpenElsewhere';
import { ORAL_EXAM_ROOM_OPTIONS } from '@/common/components/meeting/liveKitRoomOptions';
import {
  disconnectReasonName,
  liveKitDisconnectAction,
} from '@/common/components/meeting/livekitDisconnectObservability';
import log from '@/common/utils/logger';
import { isCohortExamSurface } from '@/common/utils/oralExam/runConduct';
import { OralExamReadiness } from '@/common/api/models/oralExamReadiness';
import type { OralExamReadiness as OralExamReadinessType } from '@/common/api/models/oralExamReadiness';
import { isSupervisorEngagedLecturerPause } from '@/common/oralExam/supervisionPause';
import type { ColleagueVisitFlag } from '@/common/oralExam/supervisorVisits';
import { OralExamSupervisorStatus } from '@/common/api/models/oralExamSupervisorStatus';
import { getOralExamApiErrorMessage } from '@/common/hooks/oralExam/oralExamApiError';
import { useLiveKitTokenRequest } from '@/common/hooks/oralExam/useLiveKitTokenRequest';
import { useOralExamQuestionTimer } from '@/common/hooks/oralExam/useOralExamQuestionTimer';
import {
  LabStandInImage,
  useOralExamLabOverrides,
  type OralExamLabImageSource,
} from '@/common/oralExam/oralExamLabOverrides';
import { createLiveKitJoinHandle } from '@/common/utils/oralExam/liveKitJoin';

import {
  StudentStatusInfo,
  TileObserveButton,
  formatOutageDuration,
  isOralExamVoidRun,
  mediaIntegrityStatus,
  studentDisplayName,
  studentFirstName,
  studentMonogram,
} from './StudentStatusCard';
import { SupervisionPublishGuard } from './SupervisionPublishGuard';
import { useRemoteAudioPauseUntilObserve } from './useRemoteAudioPauseUntilObserve';

/** Skip setState after unmount (async engage/leave/rejoin handlers in tests and live navigation). */
function useMountedRef() {
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  return mountedRef;
}

const READINESS_LABEL: Record<OralExamReadinessType, { key: string; defaultValue: string }> = {
  [OralExamReadiness.not_joined]: {
    key: 'oralExam.lecturer.readiness.notJoined',
    defaultValue: 'Registered — not joined yet',
  },
  [OralExamReadiness.in_lobby]: {
    key: 'oralExam.lecturer.readiness.inLobby',
    defaultValue: 'In lobby — setting up',
  },
  [OralExamReadiness.awaiting_id_review]: {
    key: 'oralExam.lecturer.readiness.awaitingIdReview',
    defaultValue: 'Awaiting ID review',
  },
  [OralExamReadiness.id_rejected]: {
    key: 'oralExam.lecturer.readiness.idRejected',
    defaultValue: 'ID rejected — needs resubmit',
  },
  [OralExamReadiness.watching_orientation]: {
    key: 'oralExam.lecturer.readiness.watchingOrientation',
    defaultValue: 'Watching orientation',
  },
  [OralExamReadiness.ready_to_start]: {
    key: 'oralExam.lecturer.readiness.readyToStart',
    defaultValue: 'Ready to start',
  },
  [OralExamReadiness.moving_to_exam_room]: {
    key: 'oralExam.lecturer.readiness.movingToExamRoom',
    defaultValue: 'Moving to exam room',
  },
  [OralExamReadiness.waiting_for_examiner]: {
    key: 'oralExam.lecturer.readiness.waitingForExaminer',
    defaultValue: 'Waiting for AI examiner',
  },
  [OralExamReadiness.waiting_for_student]: {
    key: 'oralExam.lecturer.readiness.waitingForStudent',
    defaultValue: 'AI examiner ready — waiting for student to start',
  },
  [OralExamReadiness.starting_exam]: {
    key: 'oralExam.lecturer.readiness.startingExam',
    defaultValue: 'Both ready — starting exam',
  },
  [OralExamReadiness.in_exam]: {
    key: 'oralExam.lecturer.readiness.inExam',
    defaultValue: 'In exam',
  },
  [OralExamReadiness.not_connected]: {
    key: 'oralExam.lecturer.readiness.notConnected',
    defaultValue: 'Disconnected — not connected',
  },
  [OralExamReadiness.presence_unknown]: {
    key: 'oralExam.lecturer.readiness.presenceUnknown',
    defaultValue: 'Registered — not joined yet',
  },
  [OralExamReadiness.completed]: {
    key: 'oralExam.lecturer.readiness.completed',
    defaultValue: 'Submitted',
  },
  [OralExamReadiness.aborted]: {
    key: 'oralExam.lecturer.readiness.aborted',
    defaultValue: 'Exam ended before submission',
  },
};

/** Start gate admits only students who finished orientation and ID review. */
export function isStartGateReady(readiness: OralExamReadinessType): boolean {
  return readiness === OralExamReadiness.ready_to_start;
}

/**
 * Canonical journey state: backend `readiness` when present, otherwise an
 * explicit legacy fallback for mocks/summaries predating S1.
 */
export function resolveEffectiveReadiness(
  summary: OralExamBookingRunSummary,
): OralExamReadinessType {
  if (summary.readiness) return summary.readiness;

  const { runState } = summary;
  if (runState.state === 'completed') return OralExamReadiness.completed;
  if (runState.state === 'aborted') return OralExamReadiness.aborted;
  if (runState.conductPhase) {
    return runState.conductPhase as OralExamReadinessType;
  }
  if (summary.present !== true) return OralExamReadiness.not_joined;

  const idStatus = summary.idVerification.status;
  if (idStatus === 'rejected') return OralExamReadiness.id_rejected;
  if (idStatus === 'submitted') return OralExamReadiness.awaiting_id_review;
  if (idStatus === 'approved') {
    if (!runState.preflightLobbyReadyAt) return OralExamReadiness.in_lobby;
    const isCohort = isCohortExamSurface(runState);
    if (isCohort && !runState.orientationCompletedAt) {
      return OralExamReadiness.watching_orientation;
    }
    return OralExamReadiness.ready_to_start;
  }
  return OralExamReadiness.in_lobby;
}

export function readinessStatusTestId(
  bookingId: string,
  readiness: OralExamReadinessType,
): string {
  return `cohort-readiness-${readiness}-${bookingId}`;
}

export function serverAwaitingSubscriberMedia(readiness: OralExamReadinessType): boolean {
  return readiness === OralExamReadiness.waiting_for_examiner
    || readiness === OralExamReadiness.waiting_for_student
    || readiness === OralExamReadiness.starting_exam
    || readiness === OralExamReadiness.in_exam;
}

/** Handshake phases: keep the tile in connecting/live cycling until a track arrives. */
export function serverHandshakeMediaPending(readiness: OralExamReadinessType): boolean {
  return readiness === OralExamReadiness.waiting_for_examiner
    || readiness === OralExamReadiness.waiting_for_student
    || readiness === OralExamReadiness.starting_exam;
}

export type CohortConsoleBand =
  | 'needsYou'
  | 'inExam'
  | 'waitingExaminer'
  | 'movingRoom'
  | 'preflightActive'
  | 'registered'
  | 'submitted'
  | 'void'
  | 'notCompleted';

export function cohortConsoleBand(
  summary: OralExamBookingRunSummary,
  needsAttention: boolean,
): CohortConsoleBand {
  const readiness = resolveEffectiveReadiness(summary);
  if (readiness === OralExamReadiness.completed) return 'submitted';
  if (readiness === OralExamReadiness.aborted) {
    if (isOralExamVoidRun(summary.runState)) return 'void';
    return 'notCompleted';
  }
  if (needsAttention) return 'needsYou';

  switch (readiness) {
    case OralExamReadiness.in_exam:
      return 'inExam';
    case OralExamReadiness.waiting_for_examiner:
    case OralExamReadiness.waiting_for_student:
    case OralExamReadiness.starting_exam:
      // Keep LiveKit tile mounted through the whole in-room handshake so the
      // supervisor always has a feed/presence surface (not a registered stub).
      return 'waitingExaminer';
    case OralExamReadiness.moving_to_exam_room:
      return 'movingRoom';
    case OralExamReadiness.in_lobby:
    case OralExamReadiness.awaiting_id_review:
    case OralExamReadiness.id_rejected:
    case OralExamReadiness.watching_orientation:
    case OralExamReadiness.ready_to_start:
      return 'preflightActive';
    case OralExamReadiness.presence_unknown:
    case OralExamReadiness.not_joined:
    case OralExamReadiness.not_connected:
      // Probe-unknown and confirmed-absent both mean "booked, not in room yet"
      // from the supervisor's point of view — do not scare with "cannot confirm".
      return 'registered';
    default:
      return 'registered';
  }
}

/**
 * After in_exam (late-join cutoff), unadmitted bookings cannot enter.
 * Keep them off the live supervisor roster; wrap-up still lists every exam session.
 */
export function isLiveRosterBooking(
  summary: OralExamBookingRunSummary,
  examDoorClosed: boolean,
): boolean {
  if (!examDoorClosed) return true;
  return summary.isAdmitted === true;
}

/** How long an agent-driven Done stamp may sit before elevating to Needs you. */
export const AGENT_DONE_STUCK_MS = 12_000;

/**
 * Lecturer Engage opens a lecturer_pause on the supervision target — that pause
 * is intentional, not a call for help. Per-booking `engaged` is the persisted
 * signal that this booking is the supervision target at the speaking rung.
 */
export function isLecturerEngagedPause(summary: OralExamBookingRunSummary): boolean {
  const { runState } = summary;
  return isSupervisorEngagedLecturerPause({
    isPaused: runState.isPaused,
    pauseReason: runState.pauseReason,
    supervisorStatus: summary.supervisorStatus,
  });
}

/** Rungs the student can perceive — the lecturer's camera is published at both. */
function isPerceivableRung(status: OralExamSupervisorStatus): boolean {
  return status === OralExamSupervisorStatus.here_with_you
    || status === OralExamSupervisorStatus.engaged;
}

/**
 * Elevate a booking into the Needs you band.
 * Fault / media breach always elevate, and so does any pause the lecturer did
 * not open by engaging here.
 * studentDoneAt alone: lecturer-driven sits need a human Next immediately;
 * agent-driven sits only elevate after a grace window (agent should clear the
 * stamp within ~1s). Elevating on every Done remounts LiveKit tiles (band hop)
 * and false-alarms the supervisor while the agent is still healthy — the same
 * remount that used to tear down the lecturer's camera publish on Engage.
 */
export function needsYou(
  summary: OralExamBookingRunSummary,
  nowMs: number = Date.now(),
): boolean {
  if (summary.runState.state === 'completed') return false;
  const { runState } = summary;
  if (runState.operationalFault) return true;
  if (runState.mediaIntegrity?.intact === false) return true;
  if (runState.isPaused === true && !isLecturerEngagedPause(summary)) return true;
  if (!runState.studentDoneAt) return false;
  if (runState.driver === 'lecturer') return true;
  const stampedAt = new Date(runState.studentDoneAt).getTime();
  if (Number.isNaN(stampedAt)) return true;
  return nowMs - stampedAt >= AGENT_DONE_STUCK_MS;
}

export function preflightRosterRank(readiness: OralExamReadinessType): number {
  switch (readiness) {
    case OralExamReadiness.awaiting_id_review:
    case OralExamReadiness.id_rejected:
      return 0;
    case OralExamReadiness.in_lobby:
      return 1;
    case OralExamReadiness.watching_orientation:
    case OralExamReadiness.ready_to_start:
      return 2;
    case OralExamReadiness.not_connected:
      return 3;
    case OralExamReadiness.not_joined:
    case OralExamReadiness.presence_unknown:
      return 4;
    default:
      return 2;
  }
}

/**
 * ID photos on pre-flight review are this exam session's live capture.
 * A student who has not joined (or whose presence probe failed) must not be
 * shown leftover blobs from a prior capture — that pair is the "not joined
 * yet, but here are their photos" lie.
 */
export function shouldShowPreflightIdPhotos(
  readiness: OralExamReadinessType,
  idPhotoStatus?: string | null,
): boolean {
  const submittedOrApproved = idPhotoStatus === 'submitted' || idPhotoStatus === 'approved';
  if (readiness === OralExamReadiness.not_joined
    || readiness === OralExamReadiness.presence_unknown) {
    return submittedOrApproved;
  }
  return true;
}

export function canReviewIdInPreflight(readiness: OralExamReadinessType): boolean {
  return readiness === OralExamReadiness.in_lobby
    || readiness === OralExamReadiness.awaiting_id_review
    || readiness === OralExamReadiness.id_rejected
    || readiness === OralExamReadiness.watching_orientation
    || readiness === OralExamReadiness.ready_to_start;
}

/** 1-based question progress when the student is in exam with a drawn pool. */
export function inExamQuestionProgress(
  summary: OralExamBookingRunSummary,
): { current: number; total: number } | null {
  if (resolveEffectiveReadiness(summary) !== OralExamReadiness.in_exam) return null;
  const total = summary.runState.selectedQuestionIds?.length ?? 0;
  if (total <= 0) return null;
  return {
    current: (summary.runState.currentQuestionIndex ?? 0) + 1,
    total,
  };
}

export function ReadinessJourneyLabel({
  summary,
  size = 'xs',
  color = '$textSubtle',
}: {
  summary: OralExamBookingRunSummary;
  size?: 'xs' | 'sm' | '2xs';
  color?: string;
}) {
  const { t } = useTranslation();
  const readiness = resolveEffectiveReadiness(summary);
  const copy = READINESS_LABEL[readiness];
  const progress = inExamQuestionProgress(summary);
  const label = progress
    ? t('oralExam.lecturer.readiness.inExamWithProgress', {
      current: progress.current,
      total: progress.total,
      defaultValue: `In exam · Q${progress.current}/${progress.total}`,
    })
    : t(copy.key, { defaultValue: copy.defaultValue });

  const labelNode = (
    <Text
      size={size}
      color={color}
      testID={progress
        ? `live-tile-progress-${summary.bookingId}`
        : readinessStatusTestId(summary.bookingId, readiness)}
    >
      {label}
    </Text>
  );

  if (!progress) return labelNode;

  return (
    <Box testID={readinessStatusTestId(summary.bookingId, readiness)}>
      {labelNode}
    </Box>
  );
}

/** Supervisor wall warning when the server sees an AI answer-window stall (Slice H4). */
export function ExaminerSilentStatusLine({
  summary,
}: {
  summary: OralExamBookingRunSummary;
}) {
  const { t } = useTranslation();
  if (!summary.examinerSilent) return null;

  return (
    <Box
      bg="$statusWarningSubtle"
      borderLeftWidth={3}
      borderLeftColor="$statusWarning"
      borderRadius="$xs"
      px="$2"
      py="$1"
      testID={`live-tile-examiner-silent-${summary.bookingId}`}
    >
      <Text size="xs" color="$textOnWarningSubtle">
        {t('oralExam.lecturer.cohort.examinerNotResponding', {
          defaultValue: 'AI examiner not responding',
        })}
      </Text>
    </Box>
  );
}

export type StudentLiveTileProps = {
  summary: OralExamBookingRunSummary;
  onDropIn: (bookingId: string) => void | Promise<void>;
  onLeave?: () => Promise<void>;
  onNext?: (bookingId: string) => void;
  onRemove?: (bookingId: string) => void;
  onReset?: (bookingId: string) => void;
  /** Cancel booking and allow re-book (left-behind uncommitted attempt). */
  onCancel?: (bookingId: string) => void;
  /**
   * 'needs-you' and 'on-track' are the same card: video on top, actions in one
   * row underneath. 'submitted' is the calm success tile for completed students.
   */
  variant?: 'needs-you' | 'on-track' | 'submitted';
  /**
   * `grid` — regular 16:9 wall card on the quiet wall.
   * `zoom` — Observe hero: fills leftover pane height above compact peers.
   * `compact` — a face in the observe strip: picture, name, no buttons. The card itself switches who is watched.
   */
  layout?: 'grid' | 'zoom' | 'compact';
  /** True when this booking carries the stored visible rung (here_with_you). */
  isObserving?: boolean;
  /**
   * Toggle Observe. Leaving Observe closes the zoom and, when the student can
   * already see the supervisor, takes the supervisor off their screen.
   */
  onObserveToggle?: (bookingId: string) => void;
  /** Next student still in the exam, in roster order, wrapping to the first. */
  nextObserveBookingId?: string | null;
  rungControlsDisabled?: boolean;
  observeError?: string | null;
  returnToOverviewError?: string | null;
  /** Other supervisors' visits on this student (wall flags). */
  colleagueVisits?: ColleagueVisitFlag[];
  /** Another supervisor above observing blocks Appear / Engage. */
  visitHolder?: { holderName: string; rung: 'visible' | 'speaking' } | null;
};

// Absolute fill keeps the <video> element sized to its container rather than its
// intrinsic resolution, so it can never overflow or push sibling layout.
const FILL_VIDEO_STYLE = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  width: '100%' as const,
  height: '100%' as const,
  objectFit: 'cover' as const,
};

const CONTAIN_VIDEO_STYLE = {
  ...FILL_VIDEO_STYLE,
  objectFit: 'contain' as const,
};

type FullscreenCapableElement = {
  requestFullscreen?: () => Promise<void> | void;
  querySelector?: (selector: string) => FullscreenCapableElement | null;
};

/** Request browser fullscreen on the existing screen-share pane (single VideoTrack, native Esc). */
export function requestScreenShareFullscreen(
  node: unknown,
  testID?: string,
): void {
  const doc = (globalThis as {
    document?: { querySelector?: (selector: string) => FullscreenCapableElement | null };
  }).document;
  const byTestId = testID && typeof doc?.querySelector === 'function'
    ? doc.querySelector(`[data-testid="${testID}"]`)
    : null;
  const candidate = (
    node && typeof node === 'object' && typeof (node as FullscreenCapableElement).requestFullscreen === 'function'
      ? (node as FullscreenCapableElement)
      : byTestId
  );
  const target = candidate
    ?? (
      node && typeof node === 'object' && typeof (node as FullscreenCapableElement).querySelector === 'function'
        ? (node as FullscreenCapableElement).querySelector?.('video') ?? null
        : null
    );
  if (target && typeof target.requestFullscreen === 'function') {
    Promise.resolve(target.requestFullscreen()).catch(() => undefined);
  }
}

export type TilePresenceState =
  | 'error'
  | 'connecting'
  | 'not_joined'
  | 'camera_off'
  | 'feed_ended'
  | 'live';

export type RemoteParticipantLike = Pick<Participant, 'identity' | 'metadata'>;

/** Parse LiveKit participant metadata (flare_user_id, role, booking_id). */
export function parseLiveKitParticipantMetadata(participant: RemoteParticipantLike): {
  role?: string;
  flare_user_id?: string;
  booking_id?: string;
} {
  try {
    return JSON.parse(participant.metadata ?? '{}') as {
      role?: string;
      flare_user_id?: string;
      booking_id?: string;
    };
  } catch {
    return {};
  }
}

/** True when a remote participant is the booked student (metadata first, identity fallback). */
export function isStudentRemoteParticipant(
  participant: RemoteParticipantLike,
  studentId?: string | null,
): boolean {
  const meta = parseLiveKitParticipantMetadata(participant);
  if (meta.role === 'lecturer' || meta.role === 'agent') return false;
  if (meta.role === 'student') {
    if (studentId) {
      if (meta.flare_user_id === studentId) return true;
      if (participant.identity?.startsWith(`${studentId}:`)) return true;
      return false;
    }
    return true;
  }
  if (studentId && participant.identity?.startsWith(`${studentId}:`)) {
    return participant.identity.endsWith(':student');
  }
  if (participant.identity?.endsWith(':lecturer')) return false;
  return Boolean(participant.identity);
}

export function findStudentRemoteParticipant(
  participants: RemoteParticipantLike[],
  studentId?: string | null,
): RemoteParticipantLike | null {
  return participants.find((p) => isStudentRemoteParticipant(p, studentId)) ?? null;
}

/** First subscribed track owned by the booked student (ignores agent/lecturer order). */
export function selectStudentTrackReference(
  tracks: TrackReference[],
  studentId?: string | null,
): TrackReference | null {
  return tracks.find(
    (track) => isStudentRemoteParticipant(track.participant as RemoteParticipantLike, studentId),
  ) ?? null;
}

export function isLiveKitConnected(connectionState: ConnectionState | string): boolean {
  return connectionState === ConnectionState.Connected || connectionState === 'connected';
}

/**
 * After a live track (or any connection) this mount, a LiveKit disconnect must
 * not keep an empty room subscription alive — unmount TileMedia so the tile
 * rejoins with a freshly minted token.
 */
export function shouldUnmountTileMedia({
  hadLiveTrack,
  connectionState,
  wasConnected = false,
}: {
  hadLiveTrack: boolean;
  connectionState: ConnectionState | string;
  wasConnected?: boolean;
}): boolean {
  const disconnected = connectionState === ConnectionState.Disconnected
    || connectionState === 'disconnected';
  return (hadLiveTrack || wasConnected) && disconnected;
}

/** Backoff between tile rejoins after the supervisor's feed dropped. */
export const TILE_RECONNECT_DELAYS_MS = [2_000, 5_000, 10_000, 20_000, 30_000] as const;

/** Failed tile rejoins before manual Reconnect feed is required. */
export const TILE_RECONNECT_FAILURE_CAP = 6;

/** Feed must stay live this long before the consecutive failure counter resets. */
export const TILE_LIVE_STABLE_MS = 15_000;

export function tileReconnectDelayMs(dropsSinceLive: number): number {
  const index = Math.min(Math.max(dropsSinceLive, 0), TILE_RECONNECT_DELAYS_MS.length - 1);
  return TILE_RECONNECT_DELAYS_MS[index];
}

/**
 * Engage mounts a publishing conference on the same lecturer identity. The wall
 * tile must drop its subscribe-only room for that booking so LiveKit does not
 * kick it — and so `shouldUnmountTileMedia` does not freeze the tile as
 * "Connecting media" after Return to overview.
 */
export function shouldYieldTileMediaToConference(
  supervisorStatus: OralExamBookingRunSummary['supervisorStatus'],
): boolean {
  return supervisorStatus === OralExamSupervisorStatus.engaged;
}

export function resolveTilePresence({
  error,
  connectionState,
  studentPresent,
  hasLiveTrack,
  serverReadiness,
  awaitingFirstSubscriberTrack = false,
  hadLiveTrack = false,
}: {
  error: boolean;
  connectionState: ConnectionState | string;
  studentPresent: boolean;
  hasLiveTrack: boolean;
  serverReadiness: OralExamReadinessType;
  /** Sticky per tile mount: in_exam before first subscribed track. */
  awaitingFirstSubscriberTrack?: boolean;
  /** Sticky per tile mount: a student track was subscribed earlier this mount. */
  hadLiveTrack?: boolean;
}): TilePresenceState {
  if (error) return 'error';
  if (hasLiveTrack) return 'live';
  // Feed abort: after a live track, dead feed is silent — not join-status.
  if (hadLiveTrack) {
    if (!isLiveKitConnected(connectionState)) return 'feed_ended';
    if (serverReadiness === OralExamReadiness.in_exam && !studentPresent) return 'feed_ended';
    return 'camera_off';
  }
  if (!isLiveKitConnected(connectionState)) return 'connecting';
  // Handshake: cycle connecting ↔ live as tracks appear; never demote to a
  // non-video registered card (supervisor needs constant presence indication).
  if (serverHandshakeMediaPending(serverReadiness)) return 'connecting';
  if (serverAwaitingSubscriberMedia(serverReadiness) && !studentPresent) return 'connecting';
  if (awaitingFirstSubscriberTrack) return 'connecting';
  if (!studentPresent) return 'not_joined';
  return 'camera_off';
}

type MediaState = {
  cameraTrack: TrackReference | null;
  screenTrack: TrackReference | null;
  hasMic: boolean;
  participant: Participant | null;
  error: boolean;
  presence: TilePresenceState;
  /** True when subscribe-only LiveKitRoom is connected (listen-in requires this). */
  canListen: boolean;
  /** When true, hero shows monogram + name only (no join-status subtitle). */
  silentHero?: boolean;
  /** Camera blocked while Appear/Engage rung is active on this booking. */
  supervisionCameraBlocked?: boolean;
  retrySupervisionCamera?: () => Promise<void>;
  /** Lecturer self-preview while publishing to the student. */
  lecturerSelfPreviewTrack?: TrackReference | null;
  /** Visual Lab stand-in — skips LiveKit VideoTrack. */
  standInCameraUrl?: OralExamLabImageSource;
  standInScreenUrl?: OralExamLabImageSource;
  standInSelfPreviewUrl?: OralExamLabImageSource;
  feedPaused?: boolean;
  onReconnectFeed?: () => void;
};

const EMPTY_MEDIA: MediaState = {
  cameraTrack: null,
  screenTrack: null,
  hasMic: false,
  participant: null,
  error: false,
  presence: 'connecting',
  canListen: false,
};

/** One LiveKit <VideoTrack> filling its (relative) parent. */
function VideoFill({
  trackRef,
  objectFit = 'cover',
}: {
  trackRef: TrackReference;
  objectFit?: 'cover' | 'contain';
}) {
  return (
    <Box position="absolute" top={0} left={0} right={0} bottom={0}>
      <VideoTrack
        trackRef={trackRef}
        style={objectFit === 'contain' ? CONTAIN_VIDEO_STYLE : FILL_VIDEO_STYLE}
      />
    </Box>
  );
}

/**
 * Polished fallback shown while connecting, on error, or when the camera is off —
 * a brand-tinted field with a circular initials avatar and the student's name, so
 * a tile never reads as a broken grey rectangle (2026-06-27 rebuild).
 */
type PlaceholderPresence = Exclude<TilePresenceState, 'live'>;

function placeholderLabelKey(
  presence: PlaceholderPresence,
  serverReadiness: OralExamReadinessType,
): string {
  if (presence === 'connecting' && serverAwaitingSubscriberMedia(serverReadiness)) {
    return 'oralExam.lecturer.readiness.connectingMedia';
  }
  switch (presence) {
    case 'error':
      return 'oralExam.lecturer.cohort.feedUnavailable';
    case 'connecting':
      return 'oralExam.lecturer.cohort.connecting';
    case 'not_joined':
      return 'oralExam.lecturer.cohort.notJoined';
    case 'feed_ended':
      return 'oralExam.lecturer.cohort.feedEnded';
    default:
      return 'oralExam.lecturer.cohort.cameraOff';
  }
}

function placeholderDefaultLabel(
  presence: PlaceholderPresence,
  serverReadiness: OralExamReadinessType,
): string {
  if (presence === 'connecting' && serverAwaitingSubscriberMedia(serverReadiness)) {
    return 'Connecting media';
  }
  switch (presence) {
    case 'error':
      return 'No feed';
    case 'connecting':
      return 'Connecting…';
    case 'not_joined':
      return 'Not joined yet';
    case 'feed_ended':
      return '';
    default:
      return 'Camera off';
  }
}

function placeholderSubTestId(bookingId: string, presence: PlaceholderPresence): string | undefined {
  switch (presence) {
    case 'error':
      return `live-tile-error-${bookingId}`;
    case 'connecting':
      return `live-tile-connecting-media-${bookingId}`;
    case 'not_joined':
      return `live-tile-notjoined-${bookingId}`;
    case 'camera_off':
      return `live-tile-cameraoff-${bookingId}`;
    case 'feed_ended':
      return undefined;
    default:
      return undefined;
  }
}

function MediaPlaceholder({
  summary,
  presence,
  silent = false,
  feedPaused = false,
  onReconnectFeed,
}: {
  summary: OralExamBookingRunSummary;
  presence: PlaceholderPresence;
  silent?: boolean;
  feedPaused?: boolean;
  onReconnectFeed?: () => void;
}) {
  const { t } = useTranslation();
  const { bookingId } = summary;
  const serverReadiness = resolveEffectiveReadiness(summary);
  return (
    <Center
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="$bgBrandSubtle"
      testID={`live-tile-monogram-${bookingId}`}
    >
      <VStack space="xs" alignItems="center">
        <Center w={56} h={56} borderRadius="$full" bg="$primary600">
          <Text size="lg" color="$white" fontWeight="bold">
            {studentMonogram(summary)}
          </Text>
        </Center>
        <Text size="xs" color="$textOnBrandSubtle" fontWeight="medium">
          {studentDisplayName(summary)}
        </Text>
        {feedPaused ? (
          <VStack space="xs" alignItems="center" mt="$1">
            <Text size="2xs" color="$textHint">
              {t('oralExam.lecturer.cohort.feedPaused', { defaultValue: 'Feed paused' })}
            </Text>
            {onReconnectFeed ? (
              <Button
                size="xs"
                variant="outline"
                onPress={onReconnectFeed}
                testID={`tile-feed-reconnect-${bookingId}`}
              >
                <ButtonText>
                  {t('oralExam.lecturer.cohort.reconnectFeed', { defaultValue: 'Reconnect feed' })}
                </ButtonText>
              </Button>
            ) : null}
          </VStack>
        ) : null}
        {!silent && !feedPaused ? (
          <Text
            size="2xs"
            color={presence === 'error' ? '$statusError' : '$textHint'}
            testID={placeholderSubTestId(bookingId, presence)}
          >
            {t(
              placeholderLabelKey(presence, serverReadiness),
              { defaultValue: placeholderDefaultLabel(presence, serverReadiness) },
            )}
          </Text>
        ) : null}
      </VStack>
    </Center>
  );
}

/** Small connection-quality dot next to the LIVE badge (replaces per-tile media row). */
function ConnectionQualityDotInner({ participant }: { participant: Participant }) {
  const { quality } = useConnectionQualityIndicator({ participant });
  const color = quality === ConnectionQuality.Poor || quality === ConnectionQuality.Lost
    ? '$statusWarning'
    : quality === ConnectionQuality.Good || quality === ConnectionQuality.Excellent
      ? '$statusSuccess'
      : '$textHint';

  return (
    <Text size="2xs" color={color} testID="live-tile-connection-quality">
      ●
    </Text>
  );
}

function ConnectionQualityDot({ participant }: { participant: Participant | null }) {
  if (!participant) {
    return (
      <Text size="2xs" color="$textHint" testID="live-tile-connection-quality">
        ●
      </Text>
    );
  }
  return <ConnectionQualityDotInner participant={participant} />;
}

/** Low-emphasis overflow on the tile media/card surface — reset and destructive remove. */
function TileOverflowMenu({
  bookingId,
  onReset,
  onCancel,
  onRemove,
}: {
  bookingId: string;
  onReset?: (bookingId: string) => void;
  onCancel?: (bookingId: string) => void;
  onRemove?: (bookingId: string) => void;
}) {
  const { t } = useTranslation();
  if (!onRemove && !onReset && !onCancel) return null;

  return (
    <Menu
      placement="bottom right"
      trigger={({ ...triggerProps }) => (
        <Pressable
          {...triggerProps}
          alignItems="center"
          justifyContent="center"
          borderRadius="$full"
          bg="rgba(0,0,0,0.35)"
          style={{ minWidth: 44, minHeight: 44 }}
          accessibilityRole="button"
          accessibilityLabel={t('oralExam.lecturer.cohort.tileOverflowLabel', {
            defaultValue: 'Student actions',
          })}
          testID={`tile-overflow-${bookingId}`}
        >
          <Icon as={MoreVertical} size="sm" color="$white" />
        </Pressable>
      )}
    >
      {onCancel ? (
        <MenuItem
          key="cancel"
          textValue={t('oralExam.lecturer.cohort.cancelAndRebook', {
            defaultValue: 'Cancel & allow re-booking',
          })}
          onPress={() => onCancel(bookingId)}
          testID={`tile-overflow-cancel-${bookingId}`}
        >
          <Icon as={UserX} size="sm" mr="$2" color="$statusError" />
          <MenuItemLabel size="sm" color="$statusError">
            {t('oralExam.lecturer.cohort.cancelAndRebook', {
              defaultValue: 'Cancel & allow re-booking',
            })}
          </MenuItemLabel>
        </MenuItem>
      ) : null}
      {onReset ? (
        <MenuItem
          key="reset"
          textValue={t('oralExam.lecturer.cohort.reset', { defaultValue: 'Reset exam' })}
          onPress={() => onReset(bookingId)}
          testID={`tile-overflow-reset-${bookingId}`}
        >
          <Icon as={RotateCcw} size="sm" mr="$2" color="$textBody" />
          <MenuItemLabel size="sm">
            {t('oralExam.lecturer.cohort.reset', { defaultValue: 'Reset exam' })}
          </MenuItemLabel>
        </MenuItem>
      ) : null}
      {onRemove ? (
        <MenuItem
          key="remove"
          textValue={t('oralExam.lecturer.cohort.remove', { defaultValue: 'Remove from exam' })}
          onPress={() => onRemove(bookingId)}
          testID={`tile-overflow-remove-${bookingId}`}
        >
          <Icon as={UserMinus} size="sm" mr="$2" color="$statusError" />
          <MenuItemLabel size="sm" color="$statusError">
            {t('oralExam.lecturer.cohort.remove', { defaultValue: 'Remove from exam' })}
          </MenuItemLabel>
        </MenuItem>
      ) : null}
    </Menu>
  );
}

function NoScreenSharedPlaceholder({
  bookingId,
  fill = false,
}: {
  bookingId: string;
  fill?: boolean;
}) {
  const { t } = useTranslation();
  const label = (
    <Text size={fill ? 'sm' : '2xs'} color="$textHint" textAlign="center">
      {t('oralExam.lecturer.cohort.noScreenShared', { defaultValue: 'No screen shared' })}
    </Text>
  );
  if (fill) {
    return (
      <Center flex={1} w="$full" h="$full" bg="$bgDark" px="$3" testID={`live-tile-no-screen-pip-${bookingId}`}>
        {label}
      </Center>
    );
  }
  return (
    <Box
      position="absolute"
      bottom={8}
      right={8}
      width="34%"
      minWidth={72}
      maxWidth={148}
      aspectRatio={16 / 9}
      borderRadius="$xs"
      overflow="hidden"
      borderWidth={2}
      borderColor="$white"
      bg="$bgDark"
      zIndex={5}
      testID={`live-tile-no-screen-pip-${bookingId}`}
    >
      <Center position="absolute" top={0} left={0} right={0} bottom={0} bg="$bgDark" px="$1">
        {label}
      </Center>
    </Box>
  );
}

function LecturerSelfPreviewPip({
  bookingId,
  trackRef,
}: {
  bookingId: string;
  trackRef: TrackReference;
}) {
  const { t } = useTranslation();
  return (
    <Box
      position="absolute"
      bottom={8}
      left={8}
      width="28%"
      minWidth={64}
      maxWidth={120}
      aspectRatio={4 / 3}
      borderRadius="$xs"
      overflow="hidden"
      borderWidth={2}
      borderColor="$white"
      bg="$bgDark"
      zIndex={6}
      accessibilityLabel={t('oralExam.lecturer.cohort.selfPreviewLabel', {
        defaultValue: 'You — what the student sees',
      })}
      testID={`oral-exam-tile-self-preview-${bookingId}`}
    >
      <VideoFill trackRef={trackRef} />
    </Box>
  );
}

function TileHero({
  summary,
  media,
  objectFit = 'cover',
  split = false,
}: {
  summary: OralExamBookingRunSummary;
  media: MediaState;
  objectFit?: 'cover' | 'contain';
  split?: boolean;
}) {
  const { t } = useTranslation();
  const {
    cameraTrack, screenTrack, presence, lecturerSelfPreviewTrack,
    standInCameraUrl, standInScreenUrl, standInSelfPreviewUrl,
  } = media;
  const live = presence === 'live';
  const integrity = mediaIntegrityStatus(summary);
  const inExamConduct = summary.runState.conductPhase === OralExamConductPhase.in_exam;
  const screenPaneRef = React.useRef<unknown>(null);
  const screenPaneTestId = `live-tile-screen-pane-${summary.bookingId}`;
  const screenPipTestId = `live-tile-screen-pip-${summary.bookingId}`;

  let body: React.ReactNode;
  if (split) {
    const cameraInner = standInCameraUrl ? (
      <LabStandInImage
        source={standInCameraUrl}
        resizeMode={objectFit}
        testID={`patrol-lab-standin-camera-${summary.bookingId}`}
      />
    ) : cameraTrack ? (
      <VideoFill trackRef={cameraTrack} objectFit={objectFit} />
    ) : (
      <MediaPlaceholder
        summary={summary}
        presence={presence === 'live' ? 'camera_off' : presence}
        silent={media.silentHero === true}
        feedPaused={media.feedPaused === true}
        onReconnectFeed={media.onReconnectFeed}
      />
    );
    const screenInner = standInScreenUrl ? (
      <LabStandInImage
        source={standInScreenUrl}
        resizeMode="contain"
        testID={`patrol-lab-standin-screen-${summary.bookingId}`}
      />
    ) : screenTrack ? (
      <VideoFill trackRef={screenTrack} objectFit="contain" />
    ) : (
      <NoScreenSharedPlaceholder bookingId={summary.bookingId} fill />
    );
    body = (
      <HStack
        flex={1}
        w="$full"
        h="$full"
        minHeight={0}
        space="sm"
        testID={`oral-exam-observe-split-${summary.bookingId}`}
      >
        <Box
          flex={1}
          minWidth={0}
          minHeight={0}
          position="relative"
          overflow="hidden"
          borderRadius="$sm"
          bg="$bgDark"
          testID={`live-tile-camera-${summary.bookingId}`}
        >
          {cameraInner}
        </Box>
        <Box
          flex={1}
          minWidth={0}
          minHeight={0}
          position="relative"
          overflow="hidden"
          borderRadius="$sm"
          bg="$bgDark"
        >
          {screenTrack ? (
            <Pressable
              ref={screenPaneRef as React.Ref<never>}
              flex={1}
              w="$full"
              h="$full"
              onPress={() => requestScreenShareFullscreen(screenPaneRef.current, screenPaneTestId)}
              accessibilityRole="button"
              accessibilityLabel={t('oralExam.lecturer.cohort.zoomScreenShare', {
                defaultValue: 'Zoom shared screen',
              })}
              testID={screenPaneTestId}
            >
              {screenInner}
            </Pressable>
          ) : (
            <Box flex={1} w="$full" h="$full" testID={screenPaneTestId}>
              {screenInner}
            </Box>
          )}
        </Box>
      </HStack>
    );
  } else if (standInCameraUrl) {
    body = (
      <Box position="absolute" top={0} left={0} right={0} bottom={0} testID={`live-tile-camera-${summary.bookingId}`}>
        <LabStandInImage
          source={standInCameraUrl}
          resizeMode={objectFit}
          testID={`patrol-lab-standin-camera-${summary.bookingId}`}
        />
        {standInScreenUrl ? (
          <Box
            position="absolute"
            bottom={8}
            right={8}
            width="34%"
            minWidth={72}
            maxWidth={148}
            aspectRatio={16 / 9}
            borderRadius="$xs"
            overflow="hidden"
            borderWidth={2}
            borderColor="$white"
            bg="$bgDark"
            zIndex={5}
            testID={screenPipTestId}
          >
            <LabStandInImage
              source={standInScreenUrl}
              testID={`patrol-lab-standin-screen-${summary.bookingId}`}
            />
          </Box>
        ) : inExamConduct ? (
          <NoScreenSharedPlaceholder bookingId={summary.bookingId} />
        ) : null}
      </Box>
    );
  } else if (cameraTrack && screenTrack) {
    body = (
      <Box position="absolute" top={0} left={0} right={0} bottom={0} testID={`live-tile-camera-${summary.bookingId}`}>
        <VideoFill trackRef={cameraTrack} objectFit={objectFit} />
        <Pressable
          ref={screenPaneRef as React.Ref<never>}
          position="absolute"
          bottom={8}
          right={8}
          width="34%"
          minWidth={72}
          maxWidth={148}
          aspectRatio={16 / 9}
          borderRadius="$xs"
          overflow="hidden"
          borderWidth={2}
          borderColor="$white"
          bg="$bgDark"
          zIndex={5}
          onPress={() => requestScreenShareFullscreen(screenPaneRef.current, screenPipTestId)}
          accessibilityRole="button"
          accessibilityLabel={t('oralExam.lecturer.cohort.zoomScreenShare', {
            defaultValue: 'Zoom shared screen',
          })}
          testID={screenPipTestId}
        >
          <VideoFill trackRef={screenTrack} />
        </Pressable>
      </Box>
    );
  } else if (cameraTrack && inExamConduct) {
    body = (
      <Box position="absolute" top={0} left={0} right={0} bottom={0} testID={`live-tile-camera-${summary.bookingId}`}>
        <VideoFill trackRef={cameraTrack} objectFit={objectFit} />
        <NoScreenSharedPlaceholder bookingId={summary.bookingId} />
      </Box>
    );
  } else if (cameraTrack) {
    body = (
      <Box position="absolute" top={0} left={0} right={0} bottom={0} testID={`live-tile-camera-${summary.bookingId}`}>
        <VideoFill trackRef={cameraTrack} objectFit={objectFit} />
      </Box>
    );
  } else if (screenTrack) {
    body = (
      <Box position="absolute" top={0} left={0} right={0} bottom={0} testID={`live-tile-screen-${summary.bookingId}`}>
        <VideoFill trackRef={screenTrack} objectFit={objectFit} />
      </Box>
    );
  } else {
    const placeholderPresence = presence === 'live' ? 'camera_off' : presence;
    body = (
      <MediaPlaceholder
        summary={summary}
        presence={placeholderPresence}
        silent={media.silentHero === true}
        feedPaused={media.feedPaused === true}
        onReconnectFeed={media.onReconnectFeed}
      />
    );
  }

  return (
    <>
      {body}
      {live ? (
        <HStack
          position="absolute"
          top={6}
          left={6}
          space="xs"
          alignItems="center"
          zIndex={10}
        >
          <Badge
            size="sm"
            variant="solid"
            action="error"
            borderRadius="$xs"
            testID={`live-tile-live-${summary.bookingId}`}
          >
            <BadgeText>{t('oralExam.lecturer.cohort.liveBadge', { defaultValue: 'LIVE' })}</BadgeText>
          </Badge>
          <ConnectionQualityDot participant={media.participant} />
        </HStack>
      ) : null}
      {integrity ? (
        <Box position="absolute" top={6} right={6} zIndex={10}>
          <Badge
            size="sm"
            variant="solid"
            action="warning"
            borderRadius="$xs"
            testID={`live-tile-integrity-${summary.bookingId}`}
          >
            <BadgeText>
              {t('oralExam.lecturer.cohort.integrity.badge', {
                defaultValue: 'Integrity · {{duration}}',
                duration: formatOutageDuration(integrity.seconds),
              })}
            </BadgeText>
          </Badge>
        </Box>
      ) : null}
      {lecturerSelfPreviewTrack ? (
        <LecturerSelfPreviewPip
          bookingId={summary.bookingId}
          trackRef={lecturerSelfPreviewTrack}
        />
      ) : standInSelfPreviewUrl ? (
        <Box
          position="absolute"
          bottom={8}
          left={8}
          width="28%"
          minWidth={64}
          maxWidth={120}
          aspectRatio={4 / 3}
          borderRadius="$xs"
          overflow="hidden"
          borderWidth={2}
          borderColor="$white"
          bg="$bgDark"
          zIndex={6}
          testID={`oral-exam-tile-self-preview-${summary.bookingId}`}
        >
          <LabStandInImage
            source={standInSelfPreviewUrl}
            testID={`patrol-lab-standin-self-preview-${summary.bookingId}`}
          />
        </Box>
      ) : null}
    </>
  );
}

function SupervisionCameraBlockedBanner({
  onRetry,
}: {
  onRetry: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const mountedRef = useMountedRef();
  const [retrying, setRetrying] = React.useState(false);

  const handleRetry = async () => {
    if (mountedRef.current) setRetrying(true);
    try {
      await onRetry();
    } finally {
      if (mountedRef.current) setRetrying(false);
    }
  };

  return (
    <VStack space="xs" testID="oral-exam-tile-camera-blocked">
      <Text size="xs" color="$statusError">
        {t('oralExam.lecturer.cohort.supervisionCameraBlocked', {
          defaultValue: 'Camera blocked — students still see your photo until you enable the camera.',
        })}
      </Text>
      <Button
        size="xs"
        variant="outline"
        action="secondary"
        onPress={handleRetry}
        isDisabled={retrying}
        testID="oral-exam-tile-camera-retry"
      >
        {retrying ? <ButtonSpinner /> : null}
        <ButtonText>
          {t('oralExam.retryCamera', { defaultValue: 'Retry camera' })}
        </ButtonText>
      </Button>
    </VStack>
  );
}

type TileErrorBoundaryProps = { fallback: React.ReactNode; children: React.ReactNode };

/** Isolates one tile's LiveKit connection so a failure renders a per-tile fallback. */
class TileErrorBoundary extends React.Component<TileErrorBoundaryProps, { hasError: boolean }> {
  constructor(props: TileErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    const { hasError } = this.state;
    const { fallback, children } = this.props;
    return hasError ? fallback : children;
  }
}

/** Engaged rung chips — shown on grid cards only; visible/observe chips live in the zoom. */
function TileEngagedChips({ bookingId }: { bookingId: string }) {
  const { t } = useTranslation();

  return (
    <>
      <Badge
        size="sm"
        variant="solid"
        action="warning"
        borderRadius="$xs"
        testID={`live-tile-you-are-speaking-${bookingId}`}
      >
        <BadgeText>
          {t('oralExam.lecturer.cohort.youAreSpeaking', { defaultValue: 'You are speaking' })}
        </BadgeText>
      </Badge>
      <Badge
        size="sm"
        variant="solid"
        action="warning"
        borderRadius="$xs"
        testID={`live-tile-exam-paused-${bookingId}`}
      >
        <BadgeText>
          {t('oralExam.lecturer.cohort.examPaused', { defaultValue: 'Exam paused' })}
        </BadgeText>
      </Badge>
    </>
  );
}

function colleagueFlagLabel(
  t: (key: string, opts?: { defaultValue?: string; name?: string }) => string,
  flag: ColleagueVisitFlag,
): string {
  if (flag.rung === 'observing') {
    return t('oralExam.lecturer.cohort.colleagueObserving', {
      defaultValue: '{{name}} · Observing',
      name: flag.displayName,
    });
  }
  if (flag.rung === 'speaking') {
    return t('oralExam.lecturer.cohort.colleagueEngaged', {
      defaultValue: '{{name}} · Engaged',
      name: flag.displayName,
    });
  }
  return t('oralExam.lecturer.cohort.colleagueHereWithYou', {
    defaultValue: '{{name}} · Here with you',
    name: flag.displayName,
  });
}

function ColleagueVisitFlags({
  bookingId,
  colleagueVisits,
}: {
  bookingId: string;
  colleagueVisits: ColleagueVisitFlag[];
}) {
  const { t } = useTranslation();
  if (!colleagueVisits.length) return null;

  return (
    <>
      {colleagueVisits.map((flag) => {
        const quiet = flag.rung === 'observing';
        return (
          <Badge
            key={`${flag.lecturerId}-${flag.rung}`}
            size="sm"
            variant="outline"
            action={quiet ? 'muted' : 'info'}
            borderRadius="$xs"
            bg={quiet ? '$bgSubtle' : '$statusInfoSubtle'}
            borderWidth={1}
            borderColor={quiet ? '$borderLight' : '$statusInfo'}
            testID={`live-tile-colleague-flag-${bookingId}-${flag.lecturerId}`}
          >
            <BadgeText color={quiet ? '$textSubtle' : '$textOnInfoSubtle'}>
              {colleagueFlagLabel(t, flag)}
            </BadgeText>
          </Badge>
        );
      })}
    </>
  );
}

function visitBlockedReason(
  t: (key: string, opts?: { defaultValue?: string; name?: string }) => string,
  visitHolder: { holderName: string; rung: 'visible' | 'speaking' },
): string {
  if (visitHolder.rung === 'speaking') {
    return t('oralExam.lecturer.cohort.engageBlockedByHolder', {
      defaultValue: '{{name}} is engaged with this student',
      name: visitHolder.holderName,
    });
  }
  return t('oralExam.lecturer.cohort.appearBlockedByHolder', {
    defaultValue: '{{name}} is here with this student',
    name: visitHolder.holderName,
  });
}

/** Zoom status row: one chip for observe presence; grid cards show engaged chips only. */
function TileSupervisionChipRow({
  bookingId,
  studentName,
  hasMic,
  isObserving,
  supervisorStatus,
  layout,
  colleagueVisits = [],
}: {
  bookingId: string;
  studentName: string;
  hasMic: boolean;
  isObserving: boolean;
  supervisorStatus: OralExamSupervisorStatus;
  layout: 'grid' | 'zoom';
  colleagueVisits?: ColleagueVisitFlag[];
}) {
  const { t } = useTranslation();
  const isEngagedWithStudent = supervisorStatus === OralExamSupervisorStatus.engaged;
  const flags = (
    <ColleagueVisitFlags bookingId={bookingId} colleagueVisits={colleagueVisits} />
  );

  if (layout === 'grid') {
    if (!isEngagedWithStudent && colleagueVisits.length === 0) return null;
    return (
      <HStack space="xs" alignItems="center" flexWrap="wrap">
        {flags}
        {isEngagedWithStudent ? <TileEngagedChips bookingId={bookingId} /> : null}
      </HStack>
    );
  }

  if (isEngagedWithStudent) {
    return (
      <HStack space="xs" alignItems="center" flexWrap="wrap">
        {flags}
        <TileEngagedChips bookingId={bookingId} />
      </HStack>
    );
  }

  const studentCanSeeLecturer = supervisorStatus === OralExamSupervisorStatus.here_with_you;
  if (!studentCanSeeLecturer && colleagueVisits.length === 0 && !(isObserving && !hasMic)) {
    return null;
  }

  return (
    <HStack space="xs" alignItems="center" flexWrap="wrap">
      {flags}
      {studentCanSeeLecturer ? (
        <Badge
          size="sm"
          variant="solid"
          bg="$statusInfoSubtle"
          borderRadius="$xs"
          testID={`live-tile-observing-${bookingId}`}
        >
          <BadgeText color="$textOnInfoSubtle">
            {t('oralExam.lecturer.cohort.observePresenceChip', {
              name: studentName,
              defaultValue: `${studentName} can see you · you hear them`,
            })}
          </BadgeText>
        </Badge>
      ) : null}
      {!hasMic && isObserving ? (
        <Text size="xs" color="$textSubtle" testID={`live-tile-no-audio-${bookingId}`}>
          {t('oralExam.lecturer.cohort.noAudio', { defaultValue: 'No audio' })}
        </Text>
      ) : null}
    </HStack>
  );
}

/** Leave control when Engaged — clears speaking rung while the lecturer stays on the wall. */
function TileLeaveButton({
  bookingId,
  onLeave,
}: {
  bookingId: string;
  onLeave?: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const mountedRef = useMountedRef();
  const [isLeaving, setIsLeaving] = React.useState(false);
  const [leaveError, setLeaveError] = React.useState<string | null>(null);

  if (!onLeave) return null;

  const handleLeave = async () => {
    if (mountedRef.current) {
      setIsLeaving(true);
      setLeaveError(null);
    }
    try {
      await onLeave();
    } catch (error) {
      if (mountedRef.current) {
        setLeaveError(getOralExamApiErrorMessage(error, t('oralExam.actionFailed')));
      }
    } finally {
      if (mountedRef.current) setIsLeaving(false);
    }
  };

  return (
    <VStack space="xs" alignItems="flex-start">
      <Button
        size="sm"
        variant="outline"
        action="secondary"
        onPress={handleLeave}
        isDisabled={isLeaving}
        testID={`oral-exam-tile-leave-${bookingId}`}
      >
        {isLeaving ? <ButtonSpinner /> : null}
        <ButtonText>
          {t('oralExam.lecturer.cohort.leave', { defaultValue: 'Leave' })}
        </ButtonText>
      </Button>
      {leaveError ? (
        <Text size="xs" color="$statusError" testID={`oral-exam-tile-leave-error-${bookingId}`}>
          {leaveError}
        </Text>
      ) : null}
    </VStack>
  );
}

function TileEngageButton({
  bookingId,
  helpRequested,
  onDropIn,
  isDisabled: controlsDisabled = false,
  visitHolder,
}: {
  bookingId: string;
  helpRequested: boolean;
  onDropIn: (bookingId: string) => void | Promise<void>;
  isDisabled?: boolean;
  visitHolder?: { holderName: string; rung: 'visible' | 'speaking' } | null;
}) {
  const { t } = useTranslation();
  const { confirm } = useMeetingConfirm();
  const mountedRef = useMountedRef();
  const [isEngaging, setIsEngaging] = React.useState(false);
  const [engageError, setEngageError] = React.useState<string | null>(null);
  const blockedReason = visitHolder ? visitBlockedReason(t, visitHolder) : null;

  const handleEngage = async () => {
    if (blockedReason) return;
    if (!helpRequested) {
      const confirmed = await confirm({
        title: t('oralExam.lecturer.cohort.engagePauseConfirmTitle', {
          defaultValue: 'Engage?',
        }),
        message: t('oralExam.lecturer.cohort.engagePauseConfirmMessage', {
          defaultValue: "This will pause the clock in the student's exam. Nothing said during this engagement is part of the exam.",
        }),
        confirmLabel: t('oralExam.lecturer.cohort.engage', { defaultValue: 'Engage' }),
      });
      if (!confirmed) return;
    }
    if (mountedRef.current) {
      setIsEngaging(true);
      setEngageError(null);
    }
    try {
      await onDropIn(bookingId);
    } catch (error) {
      if (mountedRef.current) {
        setEngageError(getOralExamApiErrorMessage(error, t('oralExam.actionFailed')));
      }
    } finally {
      if (mountedRef.current) setIsEngaging(false);
    }
  };

  return (
    <VStack space="xs" alignItems="flex-start">
      <Button
        size="sm"
        variant={helpRequested ? 'solid' : 'outline'}
        action={helpRequested ? 'primary' : 'secondary'}
        onPress={handleEngage}
        isDisabled={controlsDisabled || isEngaging || Boolean(blockedReason)}
        testID={`oral-exam-wall-engage-${bookingId}`}
      >
        {isEngaging ? <ButtonSpinner /> : null}
        <ButtonText>
          {t('oralExam.lecturer.cohort.engage', { defaultValue: 'Engage' })}
        </ButtonText>
      </Button>
      {blockedReason ? (
        <Text size="xs" color="$textSubtle" testID={`oral-exam-wall-engage-blocked-${bookingId}`}>
          {blockedReason}
        </Text>
      ) : null}
      {engageError ? (
        <Text size="xs" color="$statusError" testID={`oral-exam-wall-engage-error-${bookingId}`}>
          {engageError}
        </Text>
      ) : null}
    </VStack>
  );
}

/**
 * Zoomed-observe actions live under the image. Engage and Observe next
 * student are secondary; Return to overview is primary. That Return closes
 * the zoom and, when the student can see the supervisor, takes the
 * supervisor off their screen.
 */
function ObserveActionBar({
  summary,
  onDropIn,
  onObserveToggle,
  nextObserveBookingId = null,
  rungControlsDisabled = false,
  returnToOverviewError,
  visitHolder,
}: {
  summary: OralExamBookingRunSummary;
  onDropIn: (bookingId: string) => void | Promise<void>;
  onObserveToggle?: (bookingId: string) => void;
  nextObserveBookingId?: string | null;
  rungControlsDisabled?: boolean;
  returnToOverviewError?: string | null;
  visitHolder?: { holderName: string; rung: 'visible' | 'speaking' } | null;
}) {
  const { t } = useTranslation();
  const { confirm } = useMeetingConfirm();
  const mountedRef = useMountedRef();
  const { bookingId } = summary;
  const [isEngaging, setIsEngaging] = React.useState(false);
  const [engageError, setEngageError] = React.useState<string | null>(null);
  const helpRequested = summary.runState.pauseReason === OralExamPauseReason.help_requested;
  const blockedReason = visitHolder ? visitBlockedReason(t, visitHolder) : null;
  const engageLabel = t('oralExam.lecturer.cohort.engageNamed', {
    name: studentFirstName(summary),
    defaultValue: `Engage ${studentFirstName(summary)}`,
  });

  const handleEngage = async () => {
    if (blockedReason) return;
    if (!helpRequested) {
      const confirmed = await confirm({
        title: t('oralExam.lecturer.cohort.engagePauseConfirmTitle', {
          defaultValue: 'Engage?',
        }),
        message: t('oralExam.lecturer.cohort.engagePauseConfirmMessage', {
          defaultValue: "This will pause the clock in the student's exam. Nothing said during this engagement is part of the exam.",
        }),
        confirmLabel: engageLabel,
      });
      if (!confirmed) return;
    }
    if (mountedRef.current) {
      setIsEngaging(true);
      setEngageError(null);
    }
    try {
      await onDropIn(bookingId);
    } catch (error) {
      if (mountedRef.current) {
        setEngageError(getOralExamApiErrorMessage(error, t('oralExam.actionFailed')));
      }
    } finally {
      if (mountedRef.current) setIsEngaging(false);
    }
  };

  return (
    <VStack space="xs" testID="oral-exam-observe-overlay">
      {blockedReason ? (
        <Text size="xs" color="$textSubtle" testID={`oral-exam-tile-visit-blocked-${bookingId}`}>
          {blockedReason}
        </Text>
      ) : null}
      {engageError ? (
        <Text size="xs" color="$statusError" testID="oral-exam-tile-engage-error">
          {engageError}
        </Text>
      ) : null}
      {returnToOverviewError ? (
        <Text size="xs" color="$statusError" testID="oral-exam-return-to-overview-error">
          {returnToOverviewError}
        </Text>
      ) : null}
      <HStack justifyContent="flex-end" alignItems="center" space="sm" flexWrap="wrap">
        <Button
          size="sm"
          variant={helpRequested ? 'solid' : 'outline'}
          action={helpRequested ? 'primary' : 'secondary'}
          onPress={handleEngage}
          isDisabled={rungControlsDisabled || isEngaging || Boolean(blockedReason)}
          testID="oral-exam-tile-engage"
        >
          {isEngaging ? <ButtonSpinner /> : null}
          <ButtonText>
            {engageLabel}
          </ButtonText>
        </Button>
        {nextObserveBookingId && onObserveToggle ? (
          <Button
            size="sm"
            variant="outline"
            action="secondary"
            onPress={() => onObserveToggle(nextObserveBookingId)}
            isDisabled={rungControlsDisabled}
            testID="oral-exam-observe-next"
          >
            <ButtonText>
              {t('oralExam.lecturer.cohort.observeNextStudent', {
                defaultValue: 'Observe next student',
              })}
            </ButtonText>
          </Button>
        ) : null}
        <TileObserveButton
          bookingId={bookingId}
          isObserving
          onObserveToggle={onObserveToggle}
          observingLabel={t('oralExam.lecturer.cohort.backToOverview', {
            defaultValue: 'Return to overview',
          })}
          observingVariant={helpRequested ? 'outline' : 'solid'}
          observingAction={helpRequested ? 'secondary' : 'primary'}
          isDisabled={rungControlsDisabled}
          observeError={returnToOverviewError}
        />
      </HStack>
    </VStack>
  );
}

const FEED_ABORTED_MEDIA: MediaState = {
  cameraTrack: null,
  screenTrack: null,
  hasMic: false,
  participant: null,
  error: false,
  presence: 'feed_ended',
  canListen: false,
  silentHero: true,
};

/** Reads subscribed tracks inside the LiveKit room and hands them to children. */
function TileMediaInner({
  summary,
  isObserving,
  onFeedAborted,
  onFeedLive,
  onFeedLeftLive,
  children,
}: {
  summary: OralExamBookingRunSummary;
  isObserving: boolean;
  onFeedAborted?: () => void;
  onFeedLive?: () => void;
  onFeedLeftLive?: () => void;
  children: (media: MediaState) => React.ReactNode;
}) {
  const { studentId } = summary;
  const cameraTracks = useTracks([Track.Source.Camera], { onlySubscribed: true }) as TrackReference[];
  const screenTracks = useTracks([Track.Source.ScreenShare], { onlySubscribed: true }) as TrackReference[];
  const micTracks = useTracks([Track.Source.Microphone], { onlySubscribed: true }) as TrackReference[];
  const connectionState = useConnectionState();
  const remoteParticipants = useRemoteParticipants();
  const cameraTrack = selectStudentTrackReference(cameraTracks, studentId);
  const screenTrack = selectStudentTrackReference(screenTracks, studentId);
  const studentRemote = findStudentRemoteParticipant(remoteParticipants, studentId);
  const participant = (cameraTrack?.participant ?? screenTrack?.participant ?? studentRemote ?? null) as Participant | null;
  const hasLiveTrack = Boolean(cameraTrack || screenTrack);
  const hasSeenLiveTrackRef = React.useRef(false);
  if (hasLiveTrack) {
    hasSeenLiveTrackRef.current = true;
  }
  const hadLiveTrack = hasSeenLiveTrackRef.current;
  const wasConnectedRef = React.useRef(false);
  if (isLiveKitConnected(connectionState)) {
    wasConnectedRef.current = true;
  }
  const wasConnected = wasConnectedRef.current;
  const serverReadiness = resolveEffectiveReadiness(summary);
  const awaitingFirstSubscriberTrack = serverReadiness === OralExamReadiness.in_exam
    && Boolean(studentRemote)
    && !hadLiveTrack;
  const hasMic = micTracks.some(
    (track) => isStudentRemoteParticipant(track.participant as RemoteParticipantLike, studentId),
  );
  const presence = resolveTilePresence({
    error: false,
    connectionState,
    studentPresent: Boolean(studentRemote),
    hasLiveTrack,
    serverReadiness,
    awaitingFirstSubscriberTrack,
    hadLiveTrack,
  });
  const { bookingId } = summary;
  const silentHero = hadLiveTrack && presence !== 'live';
  useRemoteAudioPauseUntilObserve(isObserving);
  React.useEffect(() => {
    if (hasLiveTrack) {
      onFeedLive?.();
    } else if (hadLiveTrack) {
      onFeedLeftLive?.();
    }
  }, [hasLiveTrack, hadLiveTrack, onFeedLive, onFeedLeftLive]);
  React.useEffect(() => {
    if (shouldYieldTileMediaToConference(summary.supervisorStatus)) return;
    if (shouldUnmountTileMedia({ hadLiveTrack, connectionState, wasConnected })) {
      onFeedAborted?.();
    }
  }, [hadLiveTrack, connectionState, wasConnected, onFeedAborted, summary.supervisorStatus]);

  return (
    <SupervisionPublishGuard
      supervisorStatus={summary.supervisorStatus}
      observePublish={isObserving}
    >
      {(publish) => (
        <>
          {isObserving ? (
            <RoomAudioRenderer data-booking-id={bookingId} />
          ) : null}
          {children({
            cameraTrack,
            screenTrack,
            hasMic,
            participant,
            error: false,
            presence,
            canListen: true,
            silentHero,
            supervisionCameraBlocked: publish.cameraBlocked,
            retrySupervisionCamera: publish.retryCamera,
            lecturerSelfPreviewTrack: publish.selfPreviewTrack,
          })}
        </>
      )}
    </SupervisionPublishGuard>
  );
}

/**
 * Self-contained subscribe-only LiveKit connection for one booking. Mints a
 * lecturer token, opens the room publishing nothing, and provides the student's
 * subscribed tracks via a render prop so the card body can place the PiP hero
 * consistently whether connected, erroring, or off.
 */
function TileMedia({
  summary,
  isObserving = false,
  isRejoin = false,
  onFeedAborted,
  onFeedLive,
  onFeedLeftLive,
  ignoreDisconnectRef,
  children,
}: {
  summary: OralExamBookingRunSummary;
  isObserving?: boolean;
  /** Rejoin after a dropped feed: mint a fresh token, never reuse the cached one. */
  isRejoin?: boolean;
  onFeedAborted?: () => void;
  onFeedLive?: () => void;
  onFeedLeftLive?: () => void;
  /** When true, duplicate-identity disconnects must not surface open-elsewhere. */
  ignoreDisconnectRef?: React.MutableRefObject<boolean>;
  children: (media: MediaState) => React.ReactNode;
}) {
  const lab = useOralExamLabOverrides();
  const openElsewhereCtx = useMeetingOpenElsewhereOptional();
  const tileUnmountingRef = React.useRef(false);
  const suppressAutoRejoinRef = React.useRef(false);
  React.useEffect(() => () => {
    tileUnmountingRef.current = true;
  }, []);
  const guardedFeedAborted = React.useCallback(() => {
    if (suppressAutoRejoinRef.current) return;
    onFeedAborted?.();
  }, [onFeedAborted]);
  const standIn = lab?.resolveTileMedia?.(summary);
  const {
    bookingId, courseId, moduleId, taskId,
  } = summary;
  const handle = React.useMemo(() => {
    const joinHandle = createLiveKitJoinHandle({
      courseId, moduleId, taskId, bookingId,
    });
    // Cached creds may name a room the exam has since left (room move deletes it).
    return isRejoin
      ? { provider: joinHandle.provider, requestToken: joinHandle.requestToken }
      : joinHandle;
  }, [courseId, moduleId, taskId, bookingId, isRejoin]);
  const { joinCreds, joinError } = useLiveKitTokenRequest(standIn ? null : handle);

  if (standIn) {
    // Same flex wrappers as the LiveKitRoom path, so Visual Lab shows the real wall layout.
    return (
      <Box flex={1} minHeight={0}>
        <Box flex={1} minHeight={0} flexDirection="row">
          {children({
            ...EMPTY_MEDIA,
            presence: 'live',
            hasMic: standIn.hasMic ?? true,
            canListen: true,
            standInCameraUrl: standIn.standInCameraUrl,
            standInScreenUrl: standIn.standInScreenUrl,
            standInSelfPreviewUrl: lab?.supervisorLiveImageUrl
              && (
                summary.supervisorStatus === OralExamSupervisorStatus.here_with_you
                || summary.supervisorStatus === OralExamSupervisorStatus.engaged
              )
              ? lab.supervisorLiveImageUrl
              : undefined,
          })}
        </Box>
      </Box>
    );
  }

  if (joinError) {
    return <>{children({ ...EMPTY_MEDIA, error: true, presence: 'error' })}</>;
  }
  if (!joinCreds) return <>{children(EMPTY_MEDIA)}</>;

  const handleTileDisconnected = (reason?: DisconnectReason) => {
    if (tileUnmountingRef.current || ignoreDisconnectRef?.current) {
      return;
    }
    const action = liveKitDisconnectAction(reason, Boolean(openElsewhereCtx));
    log.warn(
      `livekit disconnected reason=${disconnectReasonName(reason)} surface=tile action=${action}`,
    );
    if (
      reason === DisconnectReason.DUPLICATE_IDENTITY
      || reason === DisconnectReason.ROOM_DELETED
      || reason === DisconnectReason.PARTICIPANT_REMOVED
    ) {
      suppressAutoRejoinRef.current = true;
      if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
        openElsewhereCtx?.reportDuplicateIdentity('tile');
      }
    }
  };

  return (
    <TileErrorBoundary fallback={children({ ...EMPTY_MEDIA, error: true, presence: 'error' })}>
      <Box flex={1} minHeight={0} testID={`oral-exam-tile-livekit-${bookingId}`}>
        <LiveKitRoom
          token={joinCreds.token}
          serverUrl={joinCreds.livekitUrl}
          connect
          audio={false}
          video={false}
          options={ORAL_EXAM_ROOM_OPTIONS}
          onError={guardedFeedAborted}
          onDisconnected={handleTileDisconnected}
          style={{ display: 'flex', flex: 1, minHeight: 0 }}
        >
          <TileMediaInner
            summary={summary}
            isObserving={isObserving}
            onFeedAborted={guardedFeedAborted}
            onFeedLive={onFeedLive}
            onFeedLeftLive={onFeedLeftLive}
          >
            {children}
          </TileMediaInner>
        </LiveKitRoom>
      </Box>
    </TileErrorBoundary>
  );
}

/**
 * Someone who has finished. A name row, not a video card — a 16:9 well
 * was taller than the live cards and pushed them out of the pane.
 */
function SubmittedCard({ summary }: { summary: OralExamBookingRunSummary }) {
  const { t } = useTranslation();
  const { bookingId } = summary;

  return (
    <HStack
      alignItems="center"
      space="sm"
      px="$3"
      py="$2"
      borderRadius="$md"
      borderWidth={1}
      borderColor="$statusSuccess"
      bg="$statusSuccessSubtle"
      w="$full"
      testID={`student-live-tile-submitted-${bookingId}`}
    >
      <Icon as={CheckCircle} size="sm" color="$statusSuccess" />
      <Text
        size="sm"
        color="$textHeading"
        fontWeight="bold"
        flexShrink={1}
        numberOfLines={1}
      >
        {studentDisplayName(summary)}
      </Text>
      <Badge
        size="sm"
        variant="solid"
        action="success"
        borderRadius="$xs"
        flexShrink={0}
        testID={`submitted-badge-${bookingId}`}
      >
        <BadgeText>
          {t('oralExam.lecturer.cohort.submittedBadge', { defaultValue: 'Submitted' })}
        </BadgeText>
      </Badge>
      <Text size="xs" color="$textSubtle" flexShrink={1} numberOfLines={1}>
        {t('oralExam.lecturer.cohort.submittedHint', {
          defaultValue: 'Exam submitted — awaiting cohort wrap-up',
        })}
      </Text>
    </HStack>
  );
}

/**
 * One row of wall actions. Engage sits immediately to the right of Observe,
 * as the primary button, when the student needs help or can already see the
 * supervisor. Return to overview, in that same row, takes the supervisor off
 * the student's screen.
 */
function WallButtonGroup({
  summary,
  canListen,
  onDropIn,
  onLeave,
  onNext,
  isObserving = false,
  onObserveToggle,
  rungControlsDisabled = false,
  observeError,
  visitHolder = null,
}: {
  summary: OralExamBookingRunSummary;
  canListen: boolean;
  onDropIn: (id: string) => void | Promise<void>;
  onLeave?: () => Promise<void>;
  onNext?: (id: string) => void;
  isObserving?: boolean;
  onObserveToggle?: (id: string) => void;
  rungControlsDisabled?: boolean;
  observeError?: string | null;
  visitHolder?: { holderName: string; rung: 'visible' | 'speaking' } | null;
}) {
  const { t } = useTranslation();
  const { bookingId, runState } = summary;
  const helpRequested = runState.pauseReason === OralExamPauseReason.help_requested;
  const isEngagedWithStudent = summary.supervisorStatus === OralExamSupervisorStatus.engaged;
  const elevated = needsYou(summary);
  const showEngage = helpRequested && !isEngagedWithStudent && !visitHolder;

  if (isEngagedWithStudent) {
    return <TileLeaveButton bookingId={bookingId} onLeave={onLeave} />;
  }

  return (
    <HStack
      justifyContent="flex-end"
      alignItems="center"
      space="sm"
      flexWrap="wrap"
      testID="tile-a-actions"
    >
      <TileObserveButton
        bookingId={bookingId}
        isObserving={isObserving}
        onObserveToggle={canListen ? onObserveToggle : undefined}
        isDisabled={rungControlsDisabled}
        observeError={observeError}
      />
      {showEngage ? (
        <TileEngageButton
          bookingId={bookingId}
          helpRequested={helpRequested}
          onDropIn={onDropIn}
          isDisabled={rungControlsDisabled}
          visitHolder={visitHolder}
        />
      ) : null}
      {elevated && onNext && !helpRequested ? (
        <Button
          size="sm"
          variant="outline"
          action="secondary"
          onPress={() => onNext(bookingId)}
          testID={`card-next-${bookingId}`}
        >
          <ButtonText>
            {t('oralExam.lecturer.cohort.next', { defaultValue: 'Next' })}
          </ButtonText>
        </Button>
      ) : null}
    </HStack>
  );
}

/** Observe-strip face: the whole card switches who is watched. */
function ObserveFacePressable({
  bookingId,
  label,
  onObserveToggle,
  children,
}: {
  bookingId: string;
  label: string;
  onObserveToggle?: (id: string) => void;
  children: React.ReactNode;
}) {
  const room = useMaybeRoomContext();
  const handlePress = React.useCallback(() => {
    if (!onObserveToggle) return;
    onObserveToggle(bookingId);
    room?.startAudio().catch(() => {});
  }, [bookingId, onObserveToggle, room]);

  return (
    <Pressable
      onPress={onObserveToggle ? handlePress : undefined}
      disabled={!onObserveToggle}
      accessibilityRole="button"
      accessibilityLabel={label}
      testID={`card-listen-${bookingId}`}
      w="$full"
    >
      {children}
    </Pressable>
  );
}

/** Wall card: live video on top; name and status on the left, actions on the right. */
function OnTrackCard({
  summary,
  media,
  onDropIn,
  onLeave,
  onNext,
  onReset,
  onCancel,
  onRemove,
  isObserving = false,
  onObserveToggle,
  nextObserveBookingId = null,
  rungControlsDisabled = false,
  observeError,
  returnToOverviewError,
  layout = 'grid',
  colleagueVisits = [],
  visitHolder = null,
}: {
  summary: OralExamBookingRunSummary;
  media: MediaState;
  onDropIn: (id: string) => void | Promise<void>;
  onLeave?: () => Promise<void>;
  onNext?: (id: string) => void;
  onReset?: (id: string) => void;
  onCancel?: (id: string) => void;
  onRemove?: (id: string) => void;
  isObserving?: boolean;
  onObserveToggle?: (id: string) => void;
  nextObserveBookingId?: string | null;
  rungControlsDisabled?: boolean;
  observeError?: string | null;
  returnToOverviewError?: string | null;
  layout?: 'grid' | 'zoom' | 'compact';
  colleagueVisits?: ColleagueVisitFlag[];
  visitHolder?: { holderName: string; rung: 'visible' | 'speaking' } | null;
}) {
  const { t } = useTranslation();
  const { bookingId, runState } = summary;
  const perceivableToStudent = isPerceivableRung(summary.supervisorStatus);
  const helpRequested = runState.pauseReason === OralExamPauseReason.help_requested;
  const inExamConduct = runState.conductPhase === OralExamConductPhase.in_exam;
  const { formatted: timerLabel } = useOralExamQuestionTimer(
    runState.secondsPerQuestion,
    inExamConduct,
    runState.questionDeadline ?? null,
    runState.isPaused ?? false,
  );

  const isZoomed = layout === 'zoom';
  const isCompact = layout === 'compact';
  const tileBorderWidth = helpRequested || (isZoomed && perceivableToStudent) ? 2 : 1;
  const tileBorderColor = helpRequested
    ? '$statusWarning'
    : (isZoomed && perceivableToStudent)
      ? '$statusInfo'
      : '$borderLight';
  const heroObjectFit = isZoomed ? 'contain' : 'cover';

  if (isCompact) {
    return (
      <ObserveFacePressable
        bookingId={bookingId}
        label={t('oralExam.lecturer.cohort.observeStudent', {
          name: studentDisplayName(summary),
          defaultValue: `Observe ${studentDisplayName(summary)}`,
        })}
        onObserveToggle={!rungControlsDisabled && media.canListen ? onObserveToggle : undefined}
      >
        <Box
          bg="$bgCard"
          borderRadius="$md"
          borderWidth={tileBorderWidth}
          borderColor={tileBorderColor}
          overflow="hidden"
          w="$full"
          testID={`student-live-tile-${bookingId}`}
        >
          <Box
            width="100%"
            aspectRatio={16 / 9}
            position="relative"
            bg="$bgDark"
            overflow="hidden"
          >
            <TileHero summary={summary} media={media} objectFit="cover" />
          </Box>
          <HStack p="$2" space="xs" alignItems="center">
            <Text
              size="sm"
              color="$textHeading"
              fontWeight="bold"
              flex={1}
              numberOfLines={1}
            >
              {studentDisplayName(summary)}
            </Text>
            {helpRequested ? (
              <Badge
                size="sm"
                variant="solid"
                action="warning"
                borderRadius="$xs"
                flexShrink={0}
                testID={`live-tile-hand-${bookingId}`}
              >
                <BadgeText>
                  {t('oralExam.lecturer.cohort.handLabel', { defaultValue: 'Hand' })}
                </BadgeText>
              </Badge>
            ) : null}
          </HStack>
          {observeError ? (
            <Text size="xs" color="$statusError" px="$2" pb="$2" testID={`card-listen-error-${bookingId}`}>
              {observeError}
            </Text>
          ) : null}
        </Box>
      </ObserveFacePressable>
    );
  }

  return (
    <Box
      bg="$bgCard"
      borderRadius="$md"
      borderWidth={tileBorderWidth}
      borderColor={tileBorderColor}
      overflow="hidden"
      position="relative"
      flex={isZoomed ? 1 : undefined}
      minHeight={isZoomed ? 0 : 0}
      w="$full"
      testID={`student-live-tile-${bookingId}`}
    >
      {isZoomed ? null : (
        <Box position="absolute" top={6} right={6} zIndex={20}>
          <TileOverflowMenu
            bookingId={bookingId}
            onReset={onReset}
            onCancel={onCancel}
            onRemove={onRemove}
          />
        </Box>
      )}
      <Box
        flex={isZoomed ? 1 : undefined}
        width="100%"
        aspectRatio={isZoomed ? undefined : 16 / 9}
        minHeight={isZoomed ? 120 : 0}
        position="relative"
        bg="$bgDark"
        overflow="hidden"
        testID={isZoomed ? `oral-exam-observe-hero-${bookingId}` : undefined}
      >
        <TileHero
          summary={summary}
          media={media}
          objectFit={heroObjectFit}
          split={isZoomed}
        />
      </Box>
      {isZoomed ? (
        <VStack p="$3" space="xs" flexShrink={0}>
          <HStack justifyContent="space-between" alignItems="center" space="sm">
            <Text
              size="md"
              color="$textHeading"
              fontWeight="bold"
              flexShrink={1}
              numberOfLines={1}
            >
              {studentDisplayName(summary)}
            </Text>
            <HStack space="sm" alignItems="center" flexShrink={0}>
              {inExamConduct ? (
                <HStack space="xs" alignItems="center">
                  <Icon as={Clock} size="xs" color="$textSubtle" />
                  <Text size="xs" color="$textSubtle">{timerLabel}</Text>
                </HStack>
              ) : null}
              <TileOverflowMenu
                bookingId={bookingId}
                onReset={onReset}
                onCancel={onCancel}
                onRemove={onRemove}
              />
            </HStack>
          </HStack>
          <ExaminerSilentStatusLine summary={summary} />
          {media.supervisionCameraBlocked && perceivableToStudent
            && media.retrySupervisionCamera
            ? (
              <SupervisionCameraBlockedBanner onRetry={media.retrySupervisionCamera} />
            )
            : null}
          <TileSupervisionChipRow
            bookingId={bookingId}
            studentName={studentDisplayName(summary)}
            hasMic={media.hasMic}
            isObserving={isObserving}
            supervisorStatus={summary.supervisorStatus}
            layout="zoom"
            colleagueVisits={colleagueVisits}
          />
          <ObserveActionBar
            summary={summary}
            onDropIn={onDropIn}
            onObserveToggle={onObserveToggle}
            nextObserveBookingId={nextObserveBookingId}
            rungControlsDisabled={rungControlsDisabled}
            returnToOverviewError={returnToOverviewError}
            visitHolder={visitHolder}
          />
        </VStack>
      ) : (
        <VStack px="$3" py="$2.5" space="xs" flexShrink={0}>
          <HStack alignItems="center" space="sm">
            <VStack flex={1} minWidth={0}>
              <Text
                size="md"
                color="$textHeading"
                fontWeight="bold"
                numberOfLines={1}
              >
                {studentDisplayName(summary)}
              </Text>
              <HStack alignItems="center" space="xs" flexWrap="wrap">
                <ReadinessJourneyLabel summary={summary} />
                {inExamConduct ? (
                  <>
                    <Text size="xs" color="$textHint">·</Text>
                    <HStack space="xs" alignItems="center">
                      <Icon as={Clock} size="2xs" color="$textSubtle" />
                      <Text size="xs" color="$textSubtle">{timerLabel}</Text>
                    </HStack>
                  </>
                ) : null}
              </HStack>
            </VStack>
            <Box flexShrink={0} maxWidth="60%">
              <WallButtonGroup
                summary={summary}
                canListen={media.canListen}
                onDropIn={onDropIn}
                onLeave={onLeave}
                onNext={onNext}
                isObserving={isObserving}
                onObserveToggle={onObserveToggle}
                rungControlsDisabled={rungControlsDisabled}
                observeError={observeError}
                visitHolder={visitHolder}
              />
            </Box>
          </HStack>
          <ExaminerSilentStatusLine summary={summary} />
          <StudentStatusInfo summary={summary} detailsOnly />
          {media.supervisionCameraBlocked && perceivableToStudent
            && media.retrySupervisionCamera
            ? (
              <SupervisionCameraBlockedBanner onRetry={media.retrySupervisionCamera} />
            )
            : null}
          <TileSupervisionChipRow
            bookingId={bookingId}
            studentName={studentDisplayName(summary)}
            hasMic={media.hasMic}
            isObserving={isObserving}
            supervisorStatus={summary.supervisorStatus}
            layout="grid"
            colleagueVisits={colleagueVisits}
          />
        </VStack>
      )}
    </Box>
  );
}

/**
 * One student tile in the cohort supervision console. Subscribe-only at rest.
 * Observing, or a visible or speaking rung, publishes this lecturer's camera
 * into the student's exam so their photo becomes that camera.
 * Every live student is the same card: video on top, one action row below.
 */
export function StudentLiveTile({
  summary,
  onDropIn,
  onLeave,
  onNext,
  onReset,
  onCancel,
  onRemove,
  variant = 'on-track',
  layout = 'grid',
  isObserving = false,
  onObserveToggle,
  nextObserveBookingId = null,
  rungControlsDisabled = false,
  observeError,
  returnToOverviewError,
  colleagueVisits = [],
  visitHolder = null,
}: StudentLiveTileProps) {
  const mountedRef = useMountedRef();
  const [feedAborted, setFeedAborted] = React.useState(false);
  const [feedRejoinPaused, setFeedRejoinPaused] = React.useState(false);
  const [rejoinCount, setRejoinCount] = React.useState(0);
  const dropsSinceLiveRef = React.useRef(0);
  const liveStableTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveStableEpochRef = React.useRef(0);
  const yieldToConference = shouldYieldTileMediaToConference(summary.supervisorStatus);
  const yieldedIdentityRef = React.useRef(false);
  if (yieldToConference) {
    yieldedIdentityRef.current = true;
  }
  const handleFeedAborted = React.useCallback(() => {
    setFeedAborted(true);
  }, []);
  const clearLiveStableTimer = React.useCallback(() => {
    liveStableEpochRef.current += 1;
    if (liveStableTimerRef.current) {
      clearTimeout(liveStableTimerRef.current);
      liveStableTimerRef.current = null;
    }
  }, []);
  const handleFeedLeftLive = React.useCallback(() => {
    clearLiveStableTimer();
  }, [clearLiveStableTimer]);
  const handleFeedLive = React.useCallback(() => {
    if (dropsSinceLiveRef.current <= 0) {
      return;
    }
    if (liveStableTimerRef.current) {
      clearTimeout(liveStableTimerRef.current);
    }
    liveStableEpochRef.current += 1;
    const epoch = liveStableEpochRef.current;
    liveStableTimerRef.current = setTimeout(() => {
      if (liveStableEpochRef.current !== epoch || !mountedRef.current) return;
      dropsSinceLiveRef.current = 0;
      setFeedRejoinPaused(false);
    }, TILE_LIVE_STABLE_MS);
  }, [mountedRef]);

  const handleReconnectFeed = React.useCallback(() => {
    dropsSinceLiveRef.current = 0;
    setFeedRejoinPaused(false);
    setFeedAborted(false);
    setRejoinCount((n) => n + 1);
  }, []);

  React.useEffect(() => () => {
    if (liveStableTimerRef.current) {
      clearTimeout(liveStableTimerRef.current);
    }
  }, []);

  // A dropped feed is never final while the tile is on the wall: rejoin with backoff.
  React.useEffect(() => {
    if (!feedAborted || feedRejoinPaused) return undefined;
    if (dropsSinceLiveRef.current >= TILE_RECONNECT_FAILURE_CAP) {
      log.warn('tile feed paused after 6 rejoins');
      setFeedRejoinPaused(true);
      setFeedAborted(false);
      return undefined;
    }
    const delayMs = tileReconnectDelayMs(dropsSinceLiveRef.current);
    dropsSinceLiveRef.current += 1;
    log.warn(`tile rejoin attempt=${dropsSinceLiveRef.current} of ${TILE_RECONNECT_FAILURE_CAP}`);
    const timer = setTimeout(() => {
      if (!mountedRef.current) return;
      setRejoinCount((n) => n + 1);
      setFeedAborted(false);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [feedAborted, feedRejoinPaused, mountedRef]);

  React.useEffect(() => {
    if (yieldToConference) {
      return;
    }
    if (yieldedIdentityRef.current) {
      yieldedIdentityRef.current = false;
      if (mountedRef.current) {
        setFeedAborted(false);
        setRejoinCount((n) => n + 1);
      }
    }
  }, [yieldToConference, mountedRef]);

  if (variant === 'submitted') {
    return <SubmittedCard summary={summary} />;
  }

  const renderTile = (media: MediaState) => (
    <OnTrackCard
      summary={summary}
      media={media}
      onDropIn={onDropIn}
      onLeave={onLeave}
      onNext={onNext}
      onReset={onReset}
      onCancel={onCancel}
      onRemove={onRemove}
      isObserving={isObserving}
      onObserveToggle={onObserveToggle}
      nextObserveBookingId={nextObserveBookingId}
      rungControlsDisabled={rungControlsDisabled}
      observeError={observeError}
      returnToOverviewError={returnToOverviewError}
      layout={layout}
      colleagueVisits={colleagueVisits}
      visitHolder={visitHolder}
    />
  );

  if (feedRejoinPaused) {
    return renderTile({
      ...FEED_ABORTED_MEDIA,
      feedPaused: true,
      onReconnectFeed: handleReconnectFeed,
    });
  }

  if (feedAborted) {
    return renderTile(FEED_ABORTED_MEDIA);
  }

  if (yieldToConference) {
    return renderTile(EMPTY_MEDIA);
  }

  return (
    <TileMedia
      key={rejoinCount}
      summary={summary}
      isObserving={isObserving}
      isRejoin={rejoinCount > 0}
      onFeedAborted={handleFeedAborted}
      onFeedLive={handleFeedLive}
      onFeedLeftLive={handleFeedLeftLive}
      ignoreDisconnectRef={yieldedIdentityRef}
    >
      {(media) => renderTile(media)}
    </TileMedia>
  );
}
